import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import { useGoogleAuth } from '@hooks/useGoogleAuth';
import { isIdentityTokenFresh } from '@utils/socialRegistration';

export type SocialSignupFormStatus = {
  /** Set by RegisterForm when the backend rejected the identity token. */
  socialReauthRequired?: boolean;
};

/**
 * TA-11025: true once Google has delivered a user for this RegisterForm, and
 * it stays true. A re-prompt (`prompt()`) clears `userInfo`, and a cancelled
 * one leaves it cleared — yet the form is still a social sign-up with no
 * password field, so it must keep validating against the social schema.
 */
export const useIsSocialSignup = () => {
  const { userInfo } = useGoogleAuth();
  const sawSocialUser = useRef(false);
  if (userInfo) sawSocialUser.current = true;
  return sawSocialUser.current;
};

/**
 * TA-11025: Google sign-up in RegisterForm posts `userInfo.identity_token`,
 * obtained when the user tapped "Continue with Google" — possibly long before
 * they finish the profile screen. Google ID tokens expire after ~1h and the
 * backend (TA-11024) rejects stale ones.
 *
 * `runWithFreshIdentity(action)` wraps the profile screen's Continue handler:
 * when the token is stale, or RegisterForm flagged a backend rejection via
 * Formik `status`, it re-prompts Google first — from inside the button press,
 * so the web popup is not blocked — and runs `action` once a fresh token
 * lands. Otherwise it runs `action` straight away.
 *
 * Apple sign-up in RegisterForm doesn't post to users.json (it reuses the
 * token OnboardingModal already registered with), so only Google is gated.
 */
export const useSocialSignupReauth = () => {
  const { userInfo, loading, prompt } = useGoogleAuth();
  const { status, setStatus, values } = useFormikContext<{
    provider?: string;
  }>();
  const pendingAction = useRef<(() => void) | null>(null);

  const identityToken = userInfo?.identity_token;
  // `prompt()` clears userInfo, and a cancelled prompt leaves it cleared, so
  // fall back to the provider Formik captured from the original sign-in —
  // otherwise the next Continue would skip the re-prompt and post without a
  // token.
  const isGoogleSignup = (userInfo?.provider ?? values?.provider) === 'Google';
  const reauthRequired = !!(status as SocialSignupFormStatus | undefined)
    ?.socialReauthRequired;

  useEffect(() => {
    const action = pendingAction.current;
    if (!action || !isIdentityTokenFresh(identityToken)) return;
    pendingAction.current = null;
    setStatus({ ...(status ?? {}), socialReauthRequired: false });
    action();
    // Only a newly delivered token should resume the pending action.
  }, [identityToken]);

  useEffect(() => {
    // The prompt finished without a fresh token (cancelled or failed): drop
    // the action so a later, unrelated sign-in can't fire it. The next
    // Continue tap re-prompts and queues a new one.
    if (loading || isIdentityTokenFresh(identityToken)) return;
    pendingAction.current = null;
  }, [loading]);

  const runWithFreshIdentity = (action: () => void) => {
    if (
      isGoogleSignup &&
      (reauthRequired || !isIdentityTokenFresh(identityToken))
    ) {
      pendingAction.current = action;
      prompt();
      return;
    }
    action();
  };

  return { runWithFreshIdentity };
};
